import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useSiteSettings, buildWhatsAppUrl } from '@/hooks/useSiteSettings';
import { openWhatsAppWithTracking } from '@/lib/gtag';
import { MessageCircle, Phone, Check } from 'lucide-react';

export default function LeadCaptureModal({ open, onOpenChange, source, defaultMessage, extra }) {
    const [step, setStep] = useState('ask'); // ask | form | done
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState(defaultMessage || '');
    const [busy, setBusy] = useState(false);
    const s = useSiteSettings();

    const reset = () => { setStep('ask'); setBusy(false); setMessage(defaultMessage || ''); };
    const handleClose = (v) => { if (!v) reset(); onOpenChange(v); };

    const openWhatsApp = () => {
        const msg = defaultMessage || `Hi Kanbr, I am interested in your services (${source || 'general'}).`;
        const url = buildWhatsAppUrl(s.whatsapp_number, msg);
        openWhatsAppWithTracking(url, { source: `lead-modal:${source || 'unknown'}`, message: msg });
        handleClose(false);
    };

    const submitLead = async () => {
        if (!/^\d{10}$/.test(phone)) { toast.error('Enter a valid 10-digit phone'); return; }
        setBusy(true);
        try {
            await api.post('/leads', { name, phone, email, source: source || 'general', message, ...(extra || {}) });
            toast.success('Kanbr team will get back to you shortly.');
            setStep('done');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Submission failed');
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md rounded-none border border-slate-200" data-testid="lead-modal">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">Let&apos;s move forward</DialogTitle>
                    <DialogDescription>
                        Do you want to start a WhatsApp conversation with us?
                    </DialogDescription>
                </DialogHeader>

                {step === 'ask' && (
                    <div className="space-y-3 pt-1">
                        <button
                            onClick={openWhatsApp}
                            data-testid="lead-whatsapp-yes"
                            className="w-full flex items-center justify-between border border-green-200 bg-green-50 px-5 py-4 hover:bg-green-100 transition-colors"
                        >
                            <div className="text-left">
                                <div className="font-display font-semibold text-green-900">Yes, open WhatsApp</div>
                                <div className="text-xs text-green-700 mt-0.5">We&apos;ll pre-fill your requirement.</div>
                            </div>
                            <MessageCircle className="w-5 h-5 text-green-700" fill="currentColor" strokeWidth={0} />
                        </button>
                        <button
                            onClick={() => setStep('form')}
                            data-testid="lead-whatsapp-no"
                            className="w-full flex items-center justify-between border border-slate-200 bg-white px-5 py-4 hover:bg-slate-50 transition-colors"
                        >
                            <div className="text-left">
                                <div className="font-display font-semibold text-slate-900">No, register with my phone</div>
                                <div className="text-xs text-slate-600 mt-0.5">Share your number — we&apos;ll reach out.</div>
                            </div>
                            <Phone className="w-5 h-5 text-slate-700" />
                        </button>
                    </div>
                )}

                {step === 'form' && (
                    <div className="space-y-4 pt-1">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="lname" className="text-xs">Name</Label>
                                <Input id="lname" data-testid="lead-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-none h-10" />
                            </div>
                            <div>
                                <Label htmlFor="lemail" className="text-xs">Email</Label>
                                <Input id="lemail" data-testid="lead-email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-none h-10" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="lphone" className="text-xs">Phone (10-digit)</Label>
                            <Input id="lphone" data-testid="lead-phone" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} className="mt-1 rounded-none h-10" placeholder="9876543210" />
                        </div>
                        <div>
                            <Label htmlFor="lmsg" className="text-xs">Requirement</Label>
                            <Textarea id="lmsg" data-testid="lead-message" value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1 rounded-none" rows={2} />
                        </div>
                        <Button onClick={submitLead} disabled={busy} data-testid="lead-submit" className="w-full bg-blue-900 hover:bg-blue-950 rounded-none py-5">
                            {busy ? 'Submitting…' : 'Submit'}
                        </Button>
                    </div>
                )}

                {step === 'done' && (
                    <div className="text-center py-8">
                        <div className="w-14 h-14 bg-green-100 text-green-700 mx-auto flex items-center justify-center mb-3 rounded-full"><Check className="w-6 h-6" /></div>
                        <h3 className="font-display text-lg font-semibold text-slate-900">Request received</h3>
                        <p className="text-sm text-slate-600 mt-2">Kanbr team will get back to you shortly on WhatsApp &amp; email.</p>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
